import { LoadingButton } from '@mui/lab';
import {
  AlternateEmail as AlternateEmailIcon,
  ConfirmationNumberRounded as ConfirmationNumberRoundedIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ChatLoader from 'src/components/loading/chat-loader.component';
import SetupHeaderCard from 'src/components/setup/setup-header-card.component';
import IOSSwitch from 'src/components/switch/switch.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';

const defaultSettings = {
  is_enabled: true,
  welcome_message: 'Hi there! How can we help you today?',
  offline_message: 'Our team is currently offline. Please leave a message.',
  response_timeout_minutes: 15,
  send_ticket_created_email: true,
  ticket_created_template: `Hi {{name}},

Our support agents are not available right now. We created a support ticket {{ticketId}} and will respond as soon as possible.`,
};

const DEFAULT_TICKET_SENDER_NAME = 'Brand2Chat support';

const defaultMailProvider = {
  provider: 'custom_smtp',
  sender_name: DEFAULT_TICKET_SENDER_NAME,
  sender_email: '',
  test_email: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_password: '',
  has_smtp_password: false,
  smtp_security: 'starttls',
  forwarding_address: '',
  forwarding_verification_status: 'not_started',
  forwarding_verification_sent_at: null,
  forwarding_verification_expires_at: null,
  forwarding_verified_at: null,
  is_forwarding_verified: false,
  is_active: true,
  is_configured: false,
};

const refreshSetupStatus = () => {
  window.dispatchEvent(new CustomEvent('brand2chat:setup-status-refresh'));
};

export default function TicketSetup() {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoader, setSettingsLoader] = useState(true);
  const [templateSaveLoader, setTemplateSaveLoader] = useState(false);
  const [mailProvider, setMailProvider] = useState(defaultMailProvider);
  const [mailProviderLoader, setMailProviderLoader] = useState(true);
  const [mailProviderSaveLoader, setMailProviderSaveLoader] = useState(false);
  const [forwardingVerifyLoader, setForwardingVerifyLoader] = useState(false);
  const [mailProviderResetLoader, setMailProviderResetLoader] = useState(false);
  const [mailProviderResetOpen, setMailProviderResetOpen] = useState(false);
  const [ticketSetupTab, setTicketSetupTab] = useState('sending');

  const fetchSettings = useCallback(() => {
    setSettingsLoader(true);
    HttpServices.Get({
      url: userModuleURL.liveChatSettings,
      successCallback: (data) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setSettingsLoader(false);
      },
      failureCallback: (message) => {
        setSettingsLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, []);

  const fetchMailProvider = useCallback(() => {
    setMailProviderLoader(true);
    HttpServices.Get({
      url: userModuleURL.ticketMailProvider,
      successCallback: (data) => {
        const providerData = data || {};
        setMailProvider({
          ...defaultMailProvider,
          ...providerData,
          sender_name: providerData.sender_name || DEFAULT_TICKET_SENDER_NAME,
          smtp_password: '',
        });
        setMailProviderLoader(false);
        refreshSetupStatus();
      },
      failureCallback: (message) => {
        setMailProviderLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchMailProvider();
  }, [fetchSettings, fetchMailProvider]);

  const saveTicketTemplate = () => {
    setTemplateSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.liveChatSettings,
      body: settings,
      successCallback: (data) => {
        setSettings({ ...defaultSettings, ...(data || {}) });
        setTemplateSaveLoader(false);
        showToastRef.current(
          translate('live_chat_setup_page.ticket_template_saved', {
            defaultValue: 'Ticket email template saved',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setTemplateSaveLoader(false);
        message && showToastRef.current(message, 'e');
      },
    });
  };

  const insertTicketTemplateVariable = (value) => {
    setSettings((prev) => ({
      ...prev,
      ticket_created_template: `${prev.ticket_created_template || ''}${
        prev.ticket_created_template ? ' ' : ''
      }${value}`,
    }));
  };

  const updateMailProvider = (field, value) => {
    setMailProvider((prev) => ({ ...prev, [field]: value }));
  };

  const saveMailProvider = () => {
    setMailProviderSaveLoader(true);
    HttpServices.Post({
      url: userModuleURL.ticketMailProvider,
      body: { ...mailProvider, provider: 'custom_smtp', is_active: true },
      successCallback: (data) => {
        const providerData = data || {};
        setMailProvider({
          ...defaultMailProvider,
          ...providerData,
          sender_name: providerData.sender_name || DEFAULT_TICKET_SENDER_NAME,
          smtp_password: '',
        });
        setMailProviderSaveLoader(false);
        refreshSetupStatus();
        showToastRef.current('Test email sent and provider saved', 's');
      },
      failureCallback: (message) => {
        setMailProviderSaveLoader(false);
        showToastRef.current(message || 'Failed to save ticket email provider', 'e');
      },
    });
  };

  const startForwardingVerification = () => {
    setForwardingVerifyLoader(true);
    HttpServices.Post({
      url: userModuleURL.ticketForwardingVerification,
      body: {},
      successCallback: (data) => {
        const providerData = data || {};
        setMailProvider({
          ...defaultMailProvider,
          ...providerData,
          sender_name: providerData.sender_name || DEFAULT_TICKET_SENDER_NAME,
          smtp_password: '',
        });
        setForwardingVerifyLoader(false);
        refreshSetupStatus();
        showToastRef.current(
          translate('live_chat_setup_page.forwarding_verification_sent', {
            defaultValue: 'Forwarding verification email sent',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setForwardingVerifyLoader(false);
        showToastRef.current(
          message ||
            translate('live_chat_setup_page.forwarding_verification_failed', {
              defaultValue: 'Failed to send forwarding verification email',
            }),
          'e'
        );
      },
    });
  };

  const resetMailProvider = () => {
    setMailProviderResetLoader(true);
    HttpServices.Post({
      url: userModuleURL.deleteticketMailProvider,
      successCallback: (data) => {
        const providerData = data || {};
        setMailProvider({
          ...defaultMailProvider,
          ...providerData,
          sender_name: providerData.sender_name || DEFAULT_TICKET_SENDER_NAME,
          smtp_password: '',
        });
        setMailProviderResetLoader(false);
        setMailProviderResetOpen(false);
        showToastRef.current(
          translate('live_chat_setup_page.ticket_provider_reset_success', {
            defaultValue: 'SMTP configuration deleted',
          }),
          's'
        );
      },
      failureCallback: (message) => {
        setMailProviderResetLoader(false);
        showToastRef.current(
          message ||
            translate('live_chat_setup_page.ticket_provider_reset_failed', {
              defaultValue: 'Failed to delete SMTP configuration',
            }),
          'e'
        );
      },
    });
  };

  const forwardingStatus = mailProvider.forwarding_verification_status || 'not_started';
  const forwardingVerified =
    Boolean(mailProvider.is_forwarding_verified) || forwardingStatus === 'verified';
  const forwardingStatusColor = forwardingVerified
    ? 'success'
    : forwardingStatus === 'pending'
      ? 'warning'
      : 'default';
  const forwardingStatusLabel = translate(`live_chat_setup_page.forwarding_status_${forwardingStatus}`, {
    defaultValue: forwardingVerified
      ? 'Verified'
      : forwardingStatus === 'pending'
        ? 'Pending'
        : forwardingStatus === 'expired'
          ? 'Expired'
          : 'Not verified',
  });
  const providerConfigured = Boolean(mailProvider.is_configured);

  const copyForwardingAddress = () => {
    const address = String(mailProvider.forwarding_address || '').trim();
    if (!address || !navigator?.clipboard) return;
    navigator.clipboard.writeText(address);
    showToast(
      translate('live_chat_setup_page.forwarding_address_copied', {
        defaultValue: 'Forwarding address copied',
      }),
      's'
    );
  };
  return (
    <>
      <Helmet>
        <title>
          {translate('live_chat_setup_page.ticket_setup_title', { defaultValue: 'Ticket Setup' })}
        </title>
      </Helmet>

      <Box sx={{ height: 'calc(100vh - 70px)', minHeight: 0, overflow: 'hidden', pb: 2 }}>
        <Stack
          spacing={2.5}
          sx={{
            height: '100%',
            minHeight: 0,
            overflowY: 'auto',
            px: 0.75,
            mx: 'auto',
            pb: { xs: 10, md: 6 },
            scrollbarGutter: 'stable both-edges',
          }}
        >
          <SetupHeaderCard
            icon={<ConfirmationNumberRoundedIcon />}
            title={translate('live_chat_setup_page.ticket_setup_title', { defaultValue: 'Ticket Setup' })}
            subtitle={translate('live_chat_setup_page.ticket_email_provider_help', {
              defaultValue:
                'Send ticket messages with SMTP. Forward customer replies to Brand2Chat so they appear in Tickets.',
            })}
          />

          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 2, flexShrink: 0 }}
          >
            <Stack spacing={2}>
            {!mailProviderLoader && !providerConfigured && (
              <Alert severity="warning">
                {translate('live_chat_setup_page.ticket_provider_not_configured', {
                  defaultValue:
                    'Automatic ticket creation is paused. Configure and verify SMTP below; until then, timed-out live chats remain open.',
                })}
              </Alert>
            )}

            {!mailProviderLoader &&
              providerConfigured &&
              forwardingVerified && (
                <Alert severity="success">
                  {translate('live_chat_setup_page.ticket_provider_configured', {
                    defaultValue:
                      'SMTP and forwarding are verified. Timed-out live chats can become tickets.',
                  })}
                </Alert>
              )}

            {!mailProviderLoader &&
              providerConfigured &&
              !forwardingVerified && (
                <Alert severity="warning">
                  {translate('live_chat_setup_page.forwarding_not_verified_notice', {
                    defaultValue:
                      'SMTP is configured, but forwarding is not verified yet. Verify forwarding before automatic live chat tickets are created.',
                  })}
                </Alert>
              )}

            {mailProviderLoader ? (
              <ChatLoader
                label={translate('live_chat_setup_page.loading_ticket_setup', {
                  defaultValue: 'Loading ticket setup',
                })}
                description={translate('live_chat_setup_page.loading_ticket_setup_description', {
                  defaultValue: 'Preparing your support ticket experience',
                })}
              />
            ) : (
              <>
                <Tabs
                  value={ticketSetupTab}
                  onChange={(_event, value) => setTicketSetupTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 850,
                      minHeight: 44,
                    },
                  }}
                >
                  <Tab
                    value="sending"
                    label={translate('live_chat_setup_page.sending_mail_tab', {
                      defaultValue: 'Sending mail',
                    })}
                  />
                  <Tab
                    value="receiving"
                    label={translate('live_chat_setup_page.receiving_mail_tab', {
                      defaultValue: 'Receiving mail',
                    })}
                  />
                  <Tab
                    value="template"
                    label={translate('live_chat_setup_page.ticket_created_template_tab', {
                      defaultValue: 'Ticket created email template',
                    })}
                  />
                </Tabs>

                {ticketSetupTab === 'sending' && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={900}>
                        {translate('live_chat_setup_page.smtp_configuration_section', {
                          defaultValue: 'SMTP configuration',
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('live_chat_setup_page.smtp_configuration_section_help', {
                          defaultValue:
                            'Connect the support mailbox used to send ticket replies and test it before saving.',
                        })}
                      </Typography>
                    </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      label={translate('live_chat_setup_page.sender_name', {
                        defaultValue: 'Sender name',
                      })}
                      value={mailProvider.sender_name}
                      onChange={(e) => updateMailProvider('sender_name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      required
                      type="email"
                      label={translate('live_chat_setup_page.brand_support_email', {
                        defaultValue: 'Brand support email',
                      })}
                      value={mailProvider.sender_email}
                      onChange={(e) => updateMailProvider('sender_email', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      required
                      type="email"
                      label={translate('live_chat_setup_page.test_email_recipient', {
                        defaultValue: 'Test email recipient',
                      })}
                      placeholder="you@example.com"
                      value={mailProvider.test_email}
                      onChange={(e) => updateMailProvider('test_email', e.target.value)}
                      helperText={translate('live_chat_setup_page.test_email_recipient_help', {
                        defaultValue:
                          'A test message must be delivered here before these settings are saved.',
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      required
                      label={translate('live_chat_setup_page.smtp_host', {
                        defaultValue: 'SMTP host',
                      })}
                      placeholder="smtp.example.com"
                      value={mailProvider.smtp_host}
                      onChange={(e) => updateMailProvider('smtp_host', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      required
                      type="number"
                      label={translate('live_chat_setup_page.smtp_port', {
                        defaultValue: 'SMTP port',
                      })}
                      value={mailProvider.smtp_port}
                      onChange={(e) => updateMailProvider('smtp_port', Number(e.target.value))}
                      inputProps={{ min: 1, max: 65535 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      select
                      fullWidth
                      disabled={providerConfigured}
                      label={translate('live_chat_setup_page.security', {
                        defaultValue: 'Security',
                      })}
                      value={mailProvider.smtp_security}
                      onChange={(e) => updateMailProvider('smtp_security', e.target.value)}
                    >
                      <MenuItem value="starttls">STARTTLS</MenuItem>
                      <MenuItem value="tls">TLS</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled={providerConfigured}
                      type="password"
                      label={translate('live_chat_setup_page.smtp_password', {
                        defaultValue: 'SMTP password',
                      })}
                      value={mailProvider.smtp_password}
                      onChange={(e) => updateMailProvider('smtp_password', e.target.value)}
                      helperText={
                        mailProvider.has_smtp_password
                          ? translate('live_chat_setup_page.smtp_password_saved_help', {
                              defaultValue: 'A password is saved. Leave blank to keep it.',
                            })
                          : translate('live_chat_setup_page.smtp_password_help', {
                              defaultValue: 'Enter the SMTP or app password.',
                            })
                      }
                    />
                  </Grid>
                </Grid>

                {/* Save SMTP BEFORE ticket email template */}
                {!providerConfigured && (
                  <>
                    <Alert severity="info">
                      {translate('live_chat_setup_page.ticket_provider_save_note', {
                        defaultValue:
                          'Saving sends a real SMTP test message. After saving, add the forwarding address above in your mail provider and click Verify forwarding.',
                      })}
                    </Alert>

                    <Stack direction="row" justifyContent="flex-end">
                      <LoadingButton
                        variant="contained"
                        loading={mailProviderSaveLoader}
                        onClick={saveMailProvider}
                      >
                        {translate('live_chat_setup_page.test_and_save_provider', {
                          defaultValue: 'Test and save provider',
                        })}
                      </LoadingButton>
                    </Stack>
                  </>
                )}

                    {providerConfigured && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="flex-end"
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Button
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteOutlineIcon fontSize="small" />}
                          onClick={() => setMailProviderResetOpen(true)}
                        >
                          {translate('live_chat_setup_page.delete_smtp_configuration', {
                            defaultValue: 'Delete SMTP configuration',
                          })}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
                )}

                {ticketSetupTab === 'receiving' && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: 'warning.main',
                    bgcolor: 'warning.lighter',
                  }}
                >
                  <Stack spacing={1.75}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={900}>
                        {translate('live_chat_setup_page.no_reply_forwarding_section', {
                          defaultValue: 'No-reply and reply forwarding',
                        })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('live_chat_setup_page.no_reply_forwarding_section_help', {
                          defaultValue:
                            'Use this generated address as your mailbox forwarding destination so customer replies return to Tickets.',
                        })}
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                          color: 'warning.contrastText',
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <AlternateEmailIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={900}>
                          {translate('live_chat_setup_page.reply_forwarding_address', {
                            defaultValue: 'Reply forwarding address',
                          })}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            mt: 0.5,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1.5,
                            bgcolor: 'background.paper',
                            border: '1px dashed',
                            borderColor: 'warning.dark',
                          }}
                        >
                          <Typography
                            sx={{
                              minWidth: 0,
                              flex: 1,
                              fontWeight: 800,
                              wordBreak: 'break-all',
                            }}
                          >
                            {mailProvider.forwarding_address ||
                              translate('live_chat_setup_page.forwarding_address_pending', {
                                defaultValue: 'Save SMTP settings to generate this address',
                              })}
                          </Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!mailProvider.forwarding_address}
                            onClick={copyForwardingAddress}
                            aria-label={translate('live_chat_setup_page.copy_forwarding_address', {
                              defaultValue: 'Copy forwarding address',
                            })}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          sx={{ mt: 0.75 }}
                        >
                          <Chip label={forwardingStatusLabel} color={forwardingStatusColor} size="small" />
                          {providerConfigured && (
                            <LoadingButton
                              size="small"
                              variant={forwardingVerified ? 'outlined' : 'contained'}
                              loading={forwardingVerifyLoader}
                              onClick={startForwardingVerification}
                            >
                              {forwardingVerified
                                ? translate('live_chat_setup_page.resend_verify_forwarding', {
                                    defaultValue: 'Resend verify',
                                  })
                                : translate('live_chat_setup_page.verify_forwarding', {
                                    defaultValue: 'Verify forwarding',
                                  })}
                            </LoadingButton>
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          {forwardingStatus === 'pending'
                            ? translate('live_chat_setup_page.forwarding_address_pending_help', {
                                defaultValue:
                                  'Verification can take 3-5 minutes. Keep this forwarding address in your mail provider; Brand2Chat will mark it verified after the verification email is received.',
                              })
                            : translate('live_chat_setup_page.forwarding_address_help', {
                                defaultValue:
                                  'Add this address as the forwarding destination in your mail provider. Then verify forwarding so Brand2Chat can confirm replies come back correctly.',
                              })}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                </Paper>
                )}

                {ticketSetupTab === 'template' && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={900}>
                          {translate('live_chat_setup_page.ticket_email_template', {
                            defaultValue: 'Ticket created email template',
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {translate('live_chat_setup_page.ticket_email_template_help', {
                            defaultValue:
                              'Customize the email sent to visitors when a missed live chat is converted into a ticket.',
                          })}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        sx={{ m: 0, mt: { xs: 0, md: 0.25 }, gap: 1 }}
                        control={
                          <IOSSwitch
                            checked={Boolean(settings.send_ticket_created_email)}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                send_ticket_created_email: e.target.checked,
                              }))
                            }
                          />
                        }
                        label={translate('live_chat_setup_page.send_ticket_created_email', {
                          defaultValue: 'Send email to client',
                        })}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary">
                        {translate('live_chat_setup_page.insert_variable', {
                          defaultValue: 'Insert variable:',
                        })}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => insertTicketTemplateVariable('{{name}}')}
                      >
                        {translate('live_chat_setup_page.name_variable', {
                          defaultValue: 'Name',
                        })}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => insertTicketTemplateVariable('{{ticketId}}')}
                      >
                        {translate('live_chat_setup_page.ticket_id_variable', {
                          defaultValue: 'Ticket ID',
                        })}
                      </Button>
                    </Stack>

                    {settingsLoader ? (
                      <ChatLoader
                        minHeight={220}
                        label={translate('live_chat_setup_page.loading_ticket_template', {
                          defaultValue: 'Loading email template',
                        })}
                        description={translate('live_chat_setup_page.loading_ticket_template_description', {
                          defaultValue: 'Preparing your ticket notification',
                        })}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        label={translate('live_chat_setup_page.ticket_template_content', {
                          defaultValue: 'Template content',
                        })}
                        disabled={!settings.send_ticket_created_email}
                        value={settings.ticket_created_template}
                        onChange={(e) =>
                          setSettings((prev) => ({
                            ...prev,
                            ticket_created_template: e.target.value.slice(0, 1000),
                          }))
                        }
                        helperText={`${(settings.ticket_created_template || '').length}/1000`}
                      />
                    )}

                    <Stack direction="row" justifyContent="flex-end">
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={templateSaveLoader}
                        onClick={saveTicketTemplate}
                      >
                        {translate('live_chat_setup_page.save_ticket_template', {
                          defaultValue: 'Save Template',
                        })}
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Paper>
                )}
              </>
            )}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <Dialog
        open={mailProviderResetOpen}
        onClose={() => !mailProviderResetLoader && setMailProviderResetOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {translate('live_chat_setup_page.delete_smtp_configuration_title', {
            defaultValue: 'Delete SMTP configuration?',
          })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {translate('live_chat_setup_page.delete_smtp_configuration_warning', {
              defaultValue:
                'Tickets and all mails are deleted when SMTP is reset. This action cannot be undone.',
            })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button disabled={mailProviderResetLoader} onClick={() => setMailProviderResetOpen(false)}>
            {translate('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={mailProviderResetLoader}
            onClick={resetMailProvider}
          >
            {translate('live_chat_setup_page.delete_smtp_configuration_confirm', {
              defaultValue: 'Delete SMTP',
            })}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
